import { Controller, useFieldArray, useWatch } from "react-hook-form";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { IconButton } from "ui/icon-button";
import { Select } from "ui/select";

import type { BrandIdentityFormValues } from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import { FIELD_ROW_SX, FIELD_SX } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.styles";

import Icons from "assets";

type GoogleFontsFieldsProps = {
  isEditing: boolean;
  fontFamilies: string[];
};

type FontOption = { label: string; value: string };

export const GoogleFontsFields = ({ isEditing, fontFamilies }: GoogleFontsFieldsProps) => {
  const { fields, append, remove } = useFieldArray<BrandIdentityFormValues, "fonts.googleFonts">({
    name: "fonts.googleFonts",
  });
  const selectedFonts = useWatch<BrandIdentityFormValues, "fonts.googleFonts">({
    name: "fonts.googleFonts",
  });

  const getOptions = (currentFamily: string): FontOption[] =>
    fontFamilies
      .filter(
        (family) =>
          family === currentFamily || !selectedFonts.some((selected) => selected.family === family),
      )
      .map((family) => ({ label: family, value: family }));

  const handleAdd = () => append({ family: "" });

  return (
    <>
      {fields.map((row, index) => (
        <Box key={row.id} sx={FIELD_ROW_SX}>
          <Controller<BrandIdentityFormValues, `fonts.googleFonts.${number}.family`>
            name={`fonts.googleFonts.${index}.family`}
            render={({ field }) => (
              <Select<FontOption>
                selectName={field.name}
                ariaLabel={`Google font ${index + 1}`}
                label="Google font"
                options={getOptions(field.value)}
                value={field.value ? { label: field.value, value: field.value } : null}
                onChange={(option) => field.onChange(option?.value ?? "")}
                onBlur={field.onBlur}
                disabled={!isEditing}
                disableClearable
                sx={FIELD_SX}
              />
            )}
          />
          {isEditing && (
            <IconButton
              aria-label={`Remove Google font ${index + 1}`}
              variant="text"
              size="large"
              onClick={() => remove(index)}>
              <Icons.Trash />
            </IconButton>
          )}
        </Box>
      ))}
      {isEditing && (
        <Button variant="text" size="large" startIcon={<Icons.Add />} onClick={handleAdd}>
          Google font
        </Button>
      )}
    </>
  );
};
